export const ROLE_IDS = {
  ADMIN: 1,
  MANAGER: 2,
  SALES_CONSULTANT: 3,
  USER: 4,
  SALES_MANAGER: 5,
  ACCOUNTS: 6,
  COMPLIANCE: 7,
} as const;
export type UserRole = string;

// Accept legacy client/session role names without changing database roles or IDs.
export function canonicalRole(role: string): UserRole {
  const code = role.toUpperCase();
  return code === "AGENT" ? "SALES_CONSULTANT" : code;
}

export function roleFromId(roleId?: number): UserRole | undefined {
  return (Object.entries(ROLE_IDS).find(([, id]) => id === roleId)?.[0] ||
    undefined) as UserRole | undefined;
}

export function knownRoleId(role: UserRole) {
  return ROLE_IDS[canonicalRole(role) as keyof typeof ROLE_IDS];
}

export function effectiveRole(
  primaryRole: UserRole,
  assignedRoles: readonly UserRole[] = [],
): UserRole {
  const roles = new Set<UserRole>(
    [primaryRole, ...assignedRoles].map(canonicalRole),
  );
  if (roles.has("ADMIN")) return "ADMIN";
  if (roles.has("MANAGER")) return "MANAGER";
  return canonicalRole(primaryRole);
}

export function hasOrganizationScope(
  primaryRole: UserRole,
  assignedRoles: readonly UserRole[] = [],
) {
  const role = effectiveRole(primaryRole, assignedRoles);
  return role === "ADMIN" || role === "MANAGER";
}

export function hasAssignedRole(
  user: { role: string; roles?: readonly string[] },
  role: string,
) {
  return [user.role, ...(user.roles || [])].some(
    (value) => canonicalRole(value) === canonicalRole(role),
  );
}

export function consultantFollowUpsOnly(user: {
  role: string;
  roles?: readonly string[];
}) {
  const roles = [user.role, ...(user.roles || [])].map((role) =>
    role.toUpperCase(),
  );
  return (
    roles.some((role) => role === "SALES_CONSULTANT" || role === "AGENT") &&
    !roles.some((role) =>
      ["ADMIN", "MANAGER", "SALES_DIRECTOR", "SALES_MANAGER"].includes(role),
    )
  );
}
